import type {
    BulkUpdateSettingsDTO,
    CreateSettingDTO,
    ISettingsRepository,
    Setting,
    SystemIntegrationConfig,
    UpdateSettingDTO,
} from '@yawi/domain';
import type { NotificationSettings, SystemSettings } from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

// Tipos de respuesta de la API
interface ApiBrandingConfig {
  companyName?: string;
  logoUrl?: string;
  faviconUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  fontFamily?: string;
  customCss?: string;
}

interface ApiBankInfo {
  bankName: string;
  accountHolder: string;
  accountNumber: string;
  clabe: string;
  swift?: string;
  currency?: string;
  paymentInstructions?: string;
  source?: 'SECTION' | 'TENANT' | 'NONE';
}

interface ApiFiscalData {
  businessName: string;
  rfc: string;
  taxRegime: string;
  address: string;
  postalCode: string;
  city?: string;
  state?: string;
  country?: string;
  email?: string;
}

interface ApiSettingResponse {
  id: string;
  key: string;
  value: string;
  parsedValue: unknown;
  description?: string | null;
  type: string;
  isPublic: boolean;
  updatedAt: string;
}

// Respuesta de GET /settings/company-info
interface ApiCompanyInfo {
  name: string;
  legalName?: string;
  website?: string;
  address: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  contactEmail: string;
  contactPhone: string;
  supportEmail?: string;
}

// Respuesta de GET /settings/localization
interface ApiLocalization {
  timezone: string;
  language: string;
  dateFormat: string;
  timeFormat?: string;
  currency: string;
  currencySymbol?: string;
  firstDayOfWeek?: number;
}

// Respuesta de GET /settings/modules
interface ApiModulesConfig {
  payments: boolean;
  amenities: boolean;
  guests: boolean;
  vehicles: boolean;
  pets: boolean;
  documents: boolean;
  tickets: boolean;
  announcements: boolean;
  polls?: boolean;
  accessControl?: boolean;
  restrictions?: boolean;
  emergency?: boolean;
  logbook?: boolean;
  patrol?: boolean;
  directory?: boolean;
  suppliers?: boolean;
  inventory?: boolean;
  reports?: boolean;
}

// Respuesta de GET /settings/rules
interface ApiRulesConfig {
  // Financial
  maintenanceFee: number;
  lateFeePercentage?: number;
  lateFeeGraceDays?: number;
  paymentDueDay?: number;
  // Limits
  maxVehiclesPerUnit?: number;
  maxPetsPerUnit?: number;
  maxGuestsPerVisit: number;
  maxActiveGuests?: number;
  maxReservationsPerDay?: number;
  maxAdvanceReservationDays?: number;
  minCancellationHours?: number;
  // Contacts
  emergencyPhone: string;
  securityPhone?: string;
  adminPhone?: string;
  // Hours
  quietHoursStart?: string;
  quietHoursEnd?: string;
  guestEntryStart?: string;
  guestEntryEnd?: string;
  pollVotingMode?: 'PER_USER' | 'PER_UNIT';
}

// Mapea las respuestas de la API al tipo SystemSettings interno
const mapApiSettingsToSystemSettings = (
  companyInfo: ApiCompanyInfo,
  localization: ApiLocalization,
  modules: ApiModulesConfig,
  rules: ApiRulesConfig,
  branding: ApiBrandingConfig,
  bankInfo: ApiBankInfo,
  fiscalData: ApiFiscalData
): SystemSettings => {
  return {
    id: 'system-settings',
    // Identity / Company Info
    condoName: companyInfo.name,
    legalName: companyInfo.legalName,
    address: companyInfo.address,
    city: companyInfo.city,
    state: companyInfo.state,
    postalCode: companyInfo.postalCode,
    country: companyInfo.country,
    contactEmail: companyInfo.contactEmail,
    contactPhone: companyInfo.contactPhone,
    supportEmail: companyInfo.supportEmail,
    website: companyInfo.website,
    // Localization / Regional
    timezone: localization.timezone,
    language: localization.language,
    dateFormat: localization.dateFormat,
    timeFormat: localization.timeFormat,
    currency: localization.currency,
    currencySymbol: localization.currencySymbol,
    firstDayOfWeek: localization.firstDayOfWeek,
    // Rules - Financial
    defaultMaintenanceFee: rules.maintenanceFee,
    lateFeePercentage: rules.lateFeePercentage,
    lateFeeGraceDays: rules.lateFeeGraceDays,
    paymentDueDay: rules.paymentDueDay,
    // Rules - Limits
    visitorLimitPerUnit: rules.maxGuestsPerVisit,
    maxVehiclesPerUnit: rules.maxVehiclesPerUnit,
    maxPetsPerUnit: rules.maxPetsPerUnit,
    maxActiveGuests: rules.maxActiveGuests,
    maxReservationsPerDay: rules.maxReservationsPerDay,
    maxAdvanceReservationDays: rules.maxAdvanceReservationDays,
    minCancellationHours: rules.minCancellationHours,
    // Rules - Contacts
    emergencyPhone: rules.emergencyPhone,
    securityPhone: rules.securityPhone,
    adminPhone: rules.adminPhone,
    // Rules - Hours
    quietHoursStart: rules.quietHoursStart,
    quietHoursEnd: rules.quietHoursEnd,
    guestEntryStart: rules.guestEntryStart,
    guestEntryEnd: rules.guestEntryEnd,
    // Rules - Polls
    pollVotingMode: rules.pollVotingMode,
    // Branding
    branding: {
      logoUrl: branding.logoUrl,
      primaryColor: branding.primaryColor,
      appName: branding.companyName,
    },
    // Bank Info
    bankInfo: {
      bankName: bankInfo.bankName,
      accountName: bankInfo.accountHolder,
      accountNumber: bankInfo.accountNumber,
      clabe: bankInfo.clabe,
      swift: bankInfo.swift,
      currency: bankInfo.currency,
      paymentInstructions: bankInfo.paymentInstructions,
    },
    // Fiscal Data
    fiscalData: {
      rfc: fiscalData.rfc,
      businessName: fiscalData.businessName,
      taxRegime: fiscalData.taxRegime,
      zipCode: fiscalData.postalCode,
      address: fiscalData.address,
      email: fiscalData.email || '',
    },
    integrations: {},
    // Features / Modules
    features: {
      enablePayments: modules.payments,
      enableAmenities: modules.amenities,
      enableGuests: modules.guests,
      enableVehicles: modules.vehicles,
      enablePets: modules.pets,
      enableDocuments: modules.documents,
      enableTickets: modules.tickets,
      enableCommunication: modules.announcements,
      enablePolls: modules.polls,
      enableAccessControl: modules.accessControl,
      enableRestrictions: modules.restrictions,
      enableEmergency: modules.emergency,
      enableLogbook: modules.logbook,
      enablePatrol: modules.patrol,
      enableDirectory: modules.directory,
      enableSuppliers: modules.suppliers,
      enableInventory: modules.inventory,
      enableReports: modules.reports,
    },
  };
};

export class ApiSettingsRepository implements ISettingsRepository {
  // Métodos principales (compatibilidad con interfaz antigua)
  async getSettings(): Promise<SystemSettings> {
    return this.getSystemSettings();
  }

  // Implementación de ISettingsRepository completa
  async getSystemSettings(): Promise<SystemSettings> {
    const client = getApiClient();

    // Obtener todos los settings de los endpoints dedicados en paralelo
    const [companyInfo, localization, modules, rules, branding, bankInfo, fiscalData] = await Promise.all([
      client.get<ApiCompanyInfo>('v1/settings/company-info'),
      client.get<ApiLocalization>('v1/settings/localization'),
      client.get<ApiModulesConfig>('v1/settings/modules'),
      client.get<ApiRulesConfig>('v1/settings/rules'),
      client.get<ApiBrandingConfig>('v1/settings/branding'),
      client.get<ApiBankInfo>('v1/settings/bank-info'),
      client.get<ApiFiscalData>('v1/settings/fiscal-data'),
    ]);

    return mapApiSettingsToSystemSettings(companyInfo, localization, modules, rules, branding, bankInfo, fiscalData);
  }

  async updateSettings(settings: Partial<SystemSettings>): Promise<SystemSettings> {
    const client = getApiClient();
    const promises: Promise<unknown>[] = [];

    // 1. Actualizar branding si existe
    if (settings.branding || settings.condoName) {
      promises.push(
        client.put('v1/settings/branding', {
          companyName: settings.branding?.appName || settings.condoName,
          logoUrl: settings.branding?.logoUrl,
          primaryColor: settings.branding?.primaryColor,
        })
      );
    }

    // 2. Actualizar bank info si existe
    if (settings.bankInfo) {
      promises.push(
        client.put('v1/settings/bank-info', {
          bankName: settings.bankInfo.bankName,
          accountHolder: settings.bankInfo.accountName,
          accountNumber: settings.bankInfo.accountNumber,
          clabe: settings.bankInfo.clabe,
          swift: settings.bankInfo.swift,
          currency: settings.bankInfo.currency,
          paymentInstructions: settings.bankInfo.paymentInstructions,
        })
      );
    }

    // 3. Actualizar fiscal data si existe
    if (settings.fiscalData) {
      promises.push(
        client.put('v1/settings/fiscal-data', {
          businessName: settings.fiscalData.businessName,
          rfc: settings.fiscalData.rfc,
          taxRegime: settings.fiscalData.taxRegime,
          address: settings.fiscalData.address,
          postalCode: settings.fiscalData.zipCode,
        })
      );
    }

    // 4. Actualizar integraciones si existen
    if (settings.integrations) {
      for (const [name, config] of Object.entries(settings.integrations)) {
        if (config) {
          promises.push(
            client.put(`v1/settings/integrations/${name}`, {
              enabled: config.enabled,
              config: { ...config },
            })
          );
        }
      }
    }

    // 5. Actualizar settings individuales via bulk update
    const settingsToUpdate: { key: string; value: string }[] = [];

    if (settings.timezone !== undefined) {
      settingsToUpdate.push({ key: 'timezone', value: settings.timezone });
    }
    if (settings.language !== undefined) {
      settingsToUpdate.push({ key: 'language', value: settings.language });
    }
    if (settings.dateFormat !== undefined) {
      settingsToUpdate.push({ key: 'date_format', value: settings.dateFormat });
    }
    if (settings.contactEmail !== undefined) {
      settingsToUpdate.push({ key: 'contact_email', value: settings.contactEmail });
    }
    if (settings.contactPhone !== undefined) {
      settingsToUpdate.push({ key: 'contact_phone', value: settings.contactPhone });
    }
    if (settings.emergencyPhone !== undefined) {
      settingsToUpdate.push({ key: 'emergency_phone', value: settings.emergencyPhone });
    }
    if (settings.visitorLimitPerUnit !== undefined) {
      settingsToUpdate.push({ key: 'visitor_limit_per_unit', value: String(settings.visitorLimitPerUnit) });
    }
    if (settings.defaultMaintenanceFee !== undefined) {
      settingsToUpdate.push({ key: 'default_maintenance_fee', value: String(settings.defaultMaintenanceFee) });
    }

    if (settingsToUpdate.length > 0) {
      promises.push(
        client.patch('v1/settings/bulk', { settings: settingsToUpdate })
      );
    }

    // 6. Actualizar features via bulk update
    if (settings.features) {
      const featureSettings = Object.entries(settings.features).map(([key, value]) => ({
        key: `feature_${key}`,
        value: String(value),
      }));
      if (featureSettings.length > 0) {
        promises.push(
          client.patch('v1/settings/bulk', { settings: featureSettings })
        );
      }
    }

    // Ejecutar todas las actualizaciones en paralelo
    await Promise.all(promises);

    // Retornar settings actualizados
    return this.getSystemSettings();
  }

  async updateSystemSettings(settings: Partial<SystemSettings>): Promise<SystemSettings> {
    return this.updateSettings(settings);
  }

  async getBrandingConfig(): Promise<{
    companyName?: string;
    logoUrl?: string;
    faviconUrl?: string;
    primaryColor?: string;
    secondaryColor?: string;
    accentColor?: string;
    fontFamily?: string;
    customCss?: string;
  }> {
    return this.getBranding();
  }

  async updateBrandingConfig(config: Partial<{
    companyName?: string;
    logoUrl?: string;
    faviconUrl?: string;
    primaryColor?: string;
    secondaryColor?: string;
    accentColor?: string;
    fontFamily?: string;
    customCss?: string;
  }>): Promise<{
    companyName?: string;
    logoUrl?: string;
    faviconUrl?: string;
    primaryColor?: string;
    secondaryColor?: string;
    accentColor?: string;
    fontFamily?: string;
    customCss?: string;
  }> {
    await this.updateBranding(config as ApiBrandingConfig);
    return this.getBranding();
  }

  async getBankInfoConfig(): Promise<{
    bankName: string;
    accountHolder: string;
    accountNumber: string;
    clabe: string;
    swift?: string;
    currency?: string;
    paymentInstructions?: string;
  }> {
    const apiBankInfo = await this.getBankInfoFromApi();
    return {
      bankName: apiBankInfo.bankName,
      accountHolder: apiBankInfo.accountHolder,
      accountNumber: apiBankInfo.accountNumber,
      clabe: apiBankInfo.clabe,
      swift: apiBankInfo.swift,
      currency: apiBankInfo.currency,
      paymentInstructions: apiBankInfo.paymentInstructions,
    };
  }

  async updateBankInfoConfig(info: Partial<{
    bankName: string;
    accountHolder: string;
    accountNumber: string;
    clabe: string;
    swift?: string;
    currency?: string;
    paymentInstructions?: string;
  }>): Promise<{
    bankName: string;
    accountHolder: string;
    accountNumber: string;
    clabe: string;
    swift?: string;
    currency?: string;
    paymentInstructions?: string;
  }> {
    await this.updateBankInfo({
      bankName: info.bankName,
      accountName: info.accountHolder, // Mapear accountHolder a accountName
      accountNumber: info.accountNumber,
      clabe: info.clabe,
      swift: info.swift,
      currency: info.currency,
      paymentInstructions: info.paymentInstructions,
    });
    return this.getBankInfoConfig();
  }

  async getIntegrationConfig(): Promise<SystemIntegrationConfig> {
    const integrations = await this.getIntegrations();
    return {
      whatsappEnabled: integrations.whatsapp?.enabled || false,
      whatsappApiKey: undefined, // No se expone en el API
      smsEnabled: false, // No hay endpoint específico
      smsProvider: undefined,
      emailEnabled: false, // No hay endpoint específico
      emailProvider: undefined,
      pushEnabled: false, // No hay endpoint específico
    };
  }

  async updateIntegrationConfig(config: Partial<SystemIntegrationConfig>): Promise<SystemIntegrationConfig> {
    // Actualizar integraciones según corresponda
    if (config.whatsappEnabled !== undefined) {
      const client = getApiClient();
      await client.put('v1/settings/integrations/whatsapp', {
        enabled: config.whatsappEnabled,
        config: config.whatsappApiKey ? { apiKey: config.whatsappApiKey } : {},
      });
    }
    return this.getIntegrationConfig();
  }

  async getUserNotificationSettings(userId: string): Promise<NotificationSettings> {
    // Este método debería estar en NotificationRepository
    // Por ahora retornar valores por defecto
    return {
      id: `notif-settings-${userId}`,
      userId,
      pushEnabled: true,
      pushQuietStart: undefined,
      pushQuietEnd: undefined,
      emailEnabled: true,
      emailDigest: 'daily',
      smsEnabled: false,
      smsOnlyUrgent: true,
      whatsappEnabled: false,
      inAppEnabled: true,
    };
  }

  async updateUserNotificationSettings(userId: string, settings: Partial<NotificationSettings>): Promise<NotificationSettings> {
    // Este método debería estar en NotificationRepository
    const current = await this.getUserNotificationSettings(userId);
    return { ...current, ...settings };
  }

  // Métodos adicionales para branding
  async getBranding(): Promise<ApiBrandingConfig> {
    const client = getApiClient();
    return await client.get<ApiBrandingConfig>('v1/settings/branding');
  }

  async updateBranding(branding: ApiBrandingConfig): Promise<void> {
    const client = getApiClient();
    await client.put('v1/settings/branding', branding);
  }

  // Método privado para obtener bank info de la API
  private async getBankInfoFromApi(unitId?: string): Promise<ApiBankInfo> {
    const client = getApiClient();
    const query = unitId ? `?unitId=${encodeURIComponent(unitId)}` : '';
    return await client.get<ApiBankInfo>(`v1/settings/bank-info${query}`);
  }

  // Implementación de getBankInfo de la interfaz (mapea a BankInfo del dominio)
  async getBankInfo(unitId?: string): Promise<import('@yawi/types').BankInfo> {
    const apiBankInfo = await this.getBankInfoFromApi(unitId);
    return {
      bankName: apiBankInfo.bankName,
      accountName: apiBankInfo.accountHolder, // Mapear accountHolder a accountName
      accountNumber: apiBankInfo.accountNumber,
      clabe: apiBankInfo.clabe,
      swift: apiBankInfo.swift,
      currency: apiBankInfo.currency,
      paymentInstructions: apiBankInfo.paymentInstructions,
      source: apiBankInfo.source,
    };
  }

  async updateBankInfo(info: Partial<import('@yawi/types').BankInfo>): Promise<import('@yawi/types').BankInfo> {
    const client = getApiClient();
    // Mapear BankInfo a ApiBankInfo
    const apiBankInfo: Partial<ApiBankInfo> = {
      bankName: info.bankName,
      accountHolder: info.accountName, // Mapear accountName a accountHolder
      accountNumber: info.accountNumber,
      clabe: info.clabe,
      swift: info.swift,
      currency: info.currency,
      paymentInstructions: info.paymentInstructions,
    };
    await client.put('v1/settings/bank-info', apiBankInfo);
    return this.getBankInfo();
  }

  async getFiscalData(): Promise<ApiFiscalData> {
    const client = getApiClient();
    return await client.get<ApiFiscalData>('v1/settings/fiscal-data');
  }

  async updateFiscalData(fiscalData: Partial<import('@yawi/types').FiscalData>): Promise<import('@yawi/types').FiscalData> {
    const client = getApiClient();
    // Mapear FiscalData del dominio a ApiFiscalData
    const apiFiscalData: Partial<ApiFiscalData> = {
      businessName: fiscalData.businessName,
      rfc: fiscalData.rfc,
      taxRegime: fiscalData.taxRegime,
      address: fiscalData.address,
      postalCode: fiscalData.zipCode,
    };
    await client.put('v1/settings/fiscal-data', apiFiscalData);
    // Retornar el fiscal data actualizado
    const updated = await this.getFiscalData();
    return {
      rfc: updated.rfc,
      businessName: updated.businessName,
      taxRegime: updated.taxRegime,
      zipCode: updated.postalCode,
      address: updated.address,
      email: '', // El API no maneja este campo directamente
    };
  }

  // Métodos de integraciones
  async getIntegrations(): Promise<{
    stripe?: { enabled: boolean; configured: boolean };
    mercadopago?: { enabled: boolean; configured: boolean };
    conekta?: { enabled: boolean; configured: boolean };
    twilio?: { enabled: boolean; configured: boolean };
    whatsapp?: { enabled: boolean; configured: boolean };
    firebase?: { enabled: boolean; configured: boolean };
    hikcentral?: { enabled: boolean; configured: boolean };
    facturapi?: { enabled: boolean; configured: boolean };
  }> {
    const client = getApiClient();
    return await client.get('v1/settings/integrations');
  }

  async getIntegration(name: string): Promise<{
    name: string;
    enabled: boolean;
    config: Record<string, unknown>;
  }> {
    const client = getApiClient();
    return await client.get(`v1/settings/integrations/${name}`);
  }

  async updateIntegration(name: string, config: import('@yawi/types').IntegrationConfig): Promise<void> {
    const client = getApiClient();
    // Extraer enabled y el resto de la config
    const { enabled, ...restConfig } = config;
    await client.put(`v1/settings/integrations/${name}`, {
      enabled,
      config: restConfig,
    });
  }

  // Métodos adicionales para gestión de settings individuales (Admin)
  async getAllSettings(): Promise<Setting[]> {
    const client = getApiClient();
    return await client.get('v1/settings');
  }

  async getSettingByKey(key: string): Promise<Setting | null> {
    const client = getApiClient();
    try {
      return await client.get(`v1/settings/key/${key}`);
    } catch {
      return null;
    }
  }

  async createSetting(setting: CreateSettingDTO): Promise<Setting> {
    const client = getApiClient();
    return await client.post('v1/settings', setting);
  }

  async updateSetting(id: string, setting: UpdateSettingDTO): Promise<Setting> {
    const client = getApiClient();
    return await client.put(`v1/settings/${id}`, setting);
  }

  async updateSettingByKey(key: string, value: string): Promise<Setting> {
    const client = getApiClient();
    return await client.patch(`v1/settings/key/${key}`, { value });
  }

  async deleteSetting(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`v1/settings/${id}`);
  }

  async bulkUpdateSettings(updates: BulkUpdateSettingsDTO): Promise<Setting[]> {
    const client = getApiClient();
    return await client.patch('v1/settings/bulk', updates);
  }

  // Nuevos endpoints dedicados
  async updateCompanyInfo(data: import('@yawi/domain').CompanyInfoDTO): Promise<void> {
    const client = getApiClient();
    await client.put('v1/settings/company-info', data);
  }

  async updateLocalization(data: import('@yawi/domain').LocalizationDTO): Promise<void> {
    const client = getApiClient();
    await client.put('v1/settings/localization', data);
  }

  async updateModules(data: import('@yawi/domain').ModulesDTO): Promise<void> {
    const client = getApiClient();
    await client.put('v1/settings/modules', data);
  }

  async updateRules(data: import('@yawi/domain').RulesDTO): Promise<void> {
    const client = getApiClient();
    await client.put('v1/settings/rules', data);
  }
}

